import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Pagination from '../../../../UI/Pagination'

import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'

import { formatVienna, toWallClock } from '../../../../shared/datetime'
import styles from './style.module.scss'
import injectRole from '../../../HOC/injectRole'
import { connect } from 'react-redux'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import NotificationItem, { hasNotificationLayout } from '../../../../UI/NotificationItem'
import { updateProfile } from '../../../../redux/ducks/user'
import MiniHeader from '../../../../UI/MiniHeader'
import BtnFilter from '../../../../UI/BtnFilter'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { companyField } from '../../../../UI/FilterPanel/fields'
import { ReactComponent as BellIcon } from '../../../../assets/icons/notify-bell.svg'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'
import NewsAside from './components/NewsAside'

/*
 * Mitteilungen für Schüler:innen und Lehrkräfte.
 *
 * Die Seite ist zweigeteilt: links die Mitteilungen zur eigenen Junior
 * Company, rechts als schmalere Spalte die Veranstaltungen & Neuigkeiten.
 * Beides sind Nachrichten, aber von verschiedener Art — nebeneinander bleibt
 * jede erkennbar, untereinander ginge das Angebot zwischen den Aufgaben unter.
 *
 * Die Liste ist nach Tagen gegliedert: eine Mitteilung ohne Bezug zu "wann"
 * ist schwer einzuordnen, und ein Datum an jeder einzelnen Karte zu lesen ist
 * mühsam. Die jüngsten Tage heißen deshalb "Heute" und "Gestern", alles
 * Ältere trägt sein Datum.
 *
 * Ungelesene Mitteilungen bleiben für die Dauer des Besuchs hervorgehoben,
 * obwohl sie beim Öffnen der Seite als gelesen gemeldet werden — sonst wäre
 * genau die Information verschwunden, deretwegen man die Seite geöffnet hat.
 * Das gilt auch über das Blättern hinweg: Wer zurückblättert, sieht dieselben
 * Karten hervorgehoben wie vorher, obwohl der Server sie inzwischen als
 * gelesen führt.
 */

const LIMIT = 8

function Notifications(props) {
	const { t } = useTranslation()
	const [notifications, setNotifications] = useState([])
	const [notificationCount, setNotificationCount] = useState(0)
	const [activationsStatus, setActivationsStatus] = useState(null)
	const [isLoading, setLoading] = useState(true)
	const [page, setPage] = useState(0)
	const [showFilter, setShowFilter] = useState(false)
	const bodyRef = useRef(null)

	/*
	 * Nach der Junior Company einzugrenzen hat nur, wer mehrere hat: Eine
	 * Lehrkraft betreut oft ein halbes Dutzend, und deren Mitteilungen liegen
	 * in einer Liste. Für Schüler:innen gibt es genau eine Company — ein Feld
	 * mit einem einzigen Wert wäre dort nur ein Klick ins Leere.
	 */
	const isTeacher = props.role === TRoles.TEACHER

	const fields = useMemo(() => (isTeacher ? [companyField(t, { scope: 'teacher' })] : []), [isTeacher, t])
	const filters = useFilters({ storageKey: 'notifications', fields })

	// Der Server grenzt ein, nicht der Browser: Die Liste blättert, und ein
	// Filter über die geholten acht Einträge meldete "nichts gefunden", wo in
	// Wahrheit "nicht auf dieser Seite" gilt.
	const companyParam = (filters.query.company || []).join(',')

	/*
	 * Nach einem Neuladen schiebt App.js auf das Dashboard, während die Abfrage
	 * noch läuft — die Antwort träfe dann eine Seite, die es nicht mehr gibt.
	 */
	const isMounted = useRef(true)

	useEffect(() => () => (isMounted.current = false), [])

	/*
	 * Welche Mitteilungen bei diesem Besuch ungelesen waren.
	 *
	 * Beim Öffnen einer Seite werden ihre Mitteilungen als gelesen gemeldet.
	 * Wer danach zurückblättert, bekäme sie vom Server als gelesen zurück und
	 * die Hervorhebung wäre verschwunden — dabei hat sich nichts geändert
	 * außer der Seitenzahl.
	 */
	const unreadAtVisit = useRef(new Set())

	// Beim Blättern an den Anfang der Liste springen — sonst steht man auf der
	// neuen Seite mitten drin oder unterhalb ihres Endes.
	useScrollTopOnTabChange(page, bodyRef)

	const fetchNotifications = offset => {
		setLoading(true)
		http.get(TApi.COMMON_GET_NOTIFICATIONS, {
			params: {
				limit: LIMIT,
				offset,
				...(companyParam ? { company: companyParam } : {})
			}
		})
			.then(({ data }) => {
				if (!isMounted.current) return
				data.rows.forEach(item => item.isViewed === false && unreadAtVisit.current.add(item.id))
				setNotifications(data.rows)
				setNotificationCount(data.count)
			})
			.finally(() => isMounted.current && setLoading(false))
	}

	const fetchActivationsStatus = () => {
		http.get(TApi.COMMON_ACTIVATED_STATUS).then(({ data }) => {
			if (isMounted.current) setActivationsStatus(data)
		})
	}

	useEffect(() => {
		fetchNotifications(page * LIMIT)
		/* eslint-disable-next-line */
	}, [page, companyParam])

	// Eine geänderte Einschränkung setzt die Seite zurück — sonst stünde man
	// auf Seite 4 einer Liste, die nur noch eine hat.
	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	useEffect(() => {
		fetchActivationsStatus()
		/* eslint-disable-next-line */
	}, [])

	useEffect(() => {
		if (notifications.length) {
			const notSeenIds = notifications.filter(item => item.isViewed === false).map(item => item.id)

			if (notSeenIds.length)
				http.put(TApi.COMMON_MARK_READ_NOTIFICATIONS, { notSeenIds }).then(({ data }) => {
					props.updateProfile({ notifications: { messages: data.count } })
				})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notifications, page])

	const visible = useMemo(() => notifications.filter(item => hasNotificationLayout(item.type)), [notifications])

	// Aufeinanderfolgende Mitteilungen desselben Tages stehen unter einer
	// gemeinsamen Überschrift. Die Liste kommt bereits absteigend sortiert.
	const groups = useMemo(() => {
		const result = []

		visible.forEach(item => {
			const date = parseISO(item.createdAt)
			// Der Kalendertag in Wiener Zeit: Sonst zerfällt eine Gruppe je nach
			// Rechner an einer anderen Stelle in zwei.
			const day = formatVienna(item.createdAt, 'yyyy-MM-dd')
			const last = result[result.length - 1]

			if (last && last.day === day) last.items.push(item)
			else result.push({ day, date, items: [item] })
		})

		return result
	}, [visible])

	/*
	 * Die Zahl im Kopf meint den ganzen Posteingang, nicht die angezeigte
	 * Seite: Sie stand vorher für "ungelesen auf Seite 2" und sprang beim
	 * Blättern hin und her. Festgehalten wird der Stand beim Öffnen der Seite —
	 * die laufende Zahl fällt gleich darauf auf null, weil das Gelesenmelden
	 * sie senkt.
	 */
	const [unreadCount, setUnreadCount] = useState(null)

	useEffect(() => {
		if (unreadCount === null && props.unreadTotal !== undefined) setUnreadCount(props.unreadTotal)
	}, [props.unreadTotal, unreadCount])

	const dayLabel = date => {
		const distance = differenceInCalendarDays(toWallClock(new Date()), toWallClock(date))

		if (distance === 0) return t('Today')
		if (distance === 1) return t('Yesterday')

		return formatVienna(date, 'dd.MM.yyyy')
	}

	/*
	 * Geblättert wird oben und unten.
	 *
	 * Acht Mitteilungen sind acht Karten mit ganzen Absätzen darin — die Seite
	 * ist damit gut zweieinhalb Bildschirme hoch. Stünde die Leiste nur unter
	 * der Liste, müsste man jedes Mal ans Ende scrollen, um weiterzukommen,
	 * und nach dem Sprung an den Anfang wieder zurück.
	 */
	const renderPagination = place =>
		notificationCount > LIMIT && (
			<div className={clsx(styles.pagination, place === 'top' && styles.paginationTop)}>
				<Pagination
					page={page}
					label={t('Messages')}
					countAll={notificationCount}
					limit={LIMIT}
					onChange={setPage}
				/>
			</div>
		)

	return (
		<div className={styles.root}>
			<MiniHeader title={t('Messages')} bg="#f8faff">
				<div className={styles.headerContent}>
					{!!unreadCount && (
						<span className={styles.unreadChip}>
							{unreadCount} {t('unread')}
						</span>
					)}
					{isTeacher && (
						<BtnFilter width={150} onClick={() => setShowFilter(true)} count={filters.count}>
							{t('Filter')} ({filters.count})
						</BtnFilter>
					)}
				</div>
			</MiniHeader>

			<div className={styles.columns}>
				<div className={styles.body} ref={bodyRef}>
					{!isLoading && !groups.length && (
						<div className={styles.empty}>
							<span className={styles.emptyIcon}>
								<BellIcon />
							</span>
							<p className={styles.emptyTitle}>
								{filters.count
									? t('No messages found.')
									: t('You have no notifications at this time')}
							</p>
							<p className={styles.emptyHint}>
								{filters.count
									? t('No message belongs to the selected company.')
									: t('New messages about your company will appear here.')}
							</p>
						</div>
					)}

					{renderPagination('top')}

					{groups.map(group => (
						<section className={styles.group} key={group.day}>
							<h4 className={styles.groupTitle}>{dayLabel(group.date)}</h4>

							<div className={styles.list}>
								{group.items.map(item => (
									<NotificationItem
										key={item.id}
										notification={{
											createdAt: item.createdAt,
											info: item.info,
											type: item.type,
											isViewed: item.isViewed && !unreadAtVisit.current.has(item.id)
										}}
										activationsStatus={activationsStatus}
									/>
								))}
							</div>
						</section>
					))}

					{renderPagination('bottom')}
				</div>

				<NewsAside role={props.role} />
			</div>

			{isTeacher && (
				<FilterPanel
					open={showFilter}
					onClose={() => setShowFilter(false)}
					fields={fields}
					values={filters.values}
					count={filters.count}
					onChange={handleFilterChange}
					onResetAll={handleFilterReset}
				/>
			)}
		</div>
	)
}

Notifications.propTypes = {
	role: PropTypes.string.isRequired,
	unreadTotal: PropTypes.number,
	updateProfile: PropTypes.func
}

// Bewusst ohne Ersatzwert: Solange das Profil noch nicht geladen ist, steht
// hier `undefined` — und die Seite hält die Zahl erst fest, wenn sie eine hat.
// Mit `|| 0` würde sie den Ladezustand als "nichts ungelesen" festschreiben.
const mapStateToProps = state => ({
	unreadTotal: state.user.profile.notifications?.messages
})

const mapDispatchToProps = dispatch => ({
	updateProfile: value => dispatch(updateProfile(value))
})

export default injectRole(connect(mapStateToProps, mapDispatchToProps)(Notifications))
